<template>
  <Modal>
    <Modal.Title>
      <span class="flex items-center gap-2.5">
        <span
          class="flex size-[30px] items-center justify-center rounded-full bg-red-200/50"
        >
          <AlertIcon class="size-4 text-red-700" />
        </span>
        Are you sure?
      </span>
    </Modal.Title>
    <Modal.Body>
      <div class="mb-3">
        Please confirm you wish to archive
        <span class="font-medium">{{ filesCopy }}</span>
        from Dacr.
      </div>
      <div>
        You will not be able to recover
        {{ pluralize('this copyright', files.length) }}.
      </div>
    </Modal.Body>
    <Modal.Footer>
      <Button
        variant="stroke"
        :size="isMobile ? 'lg' : 'md'"
        :disabled="loading"
        @click="close()"
      >
        Cancel
      </Button>
      <Button
        variant="danger"
        :size="isMobile ? 'lg' : 'md'"
        :loading="loading"
        @click="handleClick"
      >
        Confirm Archive
      </Button>
    </Modal.Footer>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Modal, Button, useModal } from '@dacr/library'
import { AlertIcon } from '@dacr/icons'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { useArchiveFilesMutation } from '@/shared/graphql/legacy/generated'
import type { File } from '@/shared/graphql/router/graphql'
import { removeItemsFromExplorerCache } from '@/features/explorer/utils/explorerCache'
import { useExplorerSelection } from '@/features/explorer/composables/useExplorerSelection'
import { useDevice } from '@/shared/composables/useDevice'
import pluralize from 'pluralize'
import { useRoute, useRouter } from 'vue-router'

const { close } = useModal()
const { isMobile } = useDevice()

const props = defineProps<{
  files: File[]
}>()

const { mutate: archiveFiles, loading } = useArchiveFilesMutation()

const route = useRoute()
const router = useRouter()
const { clear: clearSelection } = useExplorerSelection()

const filesCopy = computed(() => {
  if (props.files.length === 1) return props.files[0].name
  return `${props.files.length} copyrights`
})

const handleClick = async () => {
  try {
    const fileIdsToArchive = props.files.map((file) => file.id)

    const response = await archiveFiles(
      {
        fileIds: fileIdsToArchive,
      },
      {
        update() {
          removeItemsFromExplorerCache(fileIdsToArchive)
        },
      }
    )

    clearSelection()

    if (response?.data?.archiveFiles.asyncProcess) {
      openModal(Modals.Success, {
        props: {
          title: 'Bulk change successful',
          html: 'Note: In some cases, it may take several minutes to process your request.',
        },
      })
    } else {
      close()
    }

    if (route.path.includes('file')) {
      router.push('/')
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes('license')) {
      openModal(Modals.Error, {
        props: {
          title: 'Cannot archive licenses',
          content: 'You cannot archive license copyrights at this time.',
          error,
        },
      })
    }
  }
}
</script>
